import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useNavigate } from 'react-router-dom';
import { useCitizen } from '../CitizenContext';
import http from '../services/http';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 1;

function tenantSlug() {
  try {
    const match = (window.location.pathname || '').match(/^\/t\/([^/]+)/i);
    if (match?.[1]) {
      localStorage.setItem('tenantSlug', match[1]);
      return match[1];
    }
    return localStorage.getItem('tenantSlug') || '';
  } catch {
    return '';
  }
}

function tenantPrefix() {
  const slug = tenantSlug();
  return slug ? `/t/${slug}` : '';
}

function stringValue(value) {
  return value == null ? '' : String(value).trim();
}

function statusOf(value) {
  return Number(
    value?.status ||
      value?.response?.status ||
      value?.data?.status ||
      0
  );
}

function responsePayload(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;
}

function errorMessage(error, fallback = 'Search failed') {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    fallback
  );
}

function isAbortError(error) {
  return (
    error?.name === 'AbortError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message === 'canceled'
  );
}

function normalizeResults(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.citizens)) return payload.citizens;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;

  if (payload.citizen && typeof payload.citizen === 'object') {
    return [payload.citizen];
  }
  if (payload.result?.citizen && typeof payload.result.citizen === 'object') {
    return [payload.result.citizen];
  }
  if (payload.data?.citizen && typeof payload.data.citizen === 'object') {
    return [payload.data.citizen];
  }
  if (payload.data && typeof payload.data === 'object') {
    return normalizeResults(payload.data);
  }

  return [];
}

function unwrapCitizen(payload) {
  const value = responsePayload(payload) || {};
  return (
    value?.citizen ||
    value?.data?.citizen ||
    value?.result?.citizen ||
    value?.data ||
    value ||
    null
  );
}

function deduplicateCitizens(items) {
  const seen = new Set();
  const output = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const citizen = unwrapCitizen(item);
    if (!citizen || typeof citizen !== 'object') return;

    const key = stringValue(
      citizen.citizenId || citizen.id || citizen._id || citizen.cid
    ).toLowerCase();
    if (!key || seen.has(key)) return;

    seen.add(key);
    output.push(citizen);
  });

  return output;
}

function buildSearchUrls(query) {
  const value = stringValue(query);
  const encoded = encodeURIComponent(value);
  const emailLike = value.includes('@');
  const mobileLike = /^\+?\d{6,15}$/.test(value.replace(/[\s()-]/g, ''));

  const canonicalParams = new URLSearchParams();
  if (emailLike) {
    canonicalParams.set('email', value.toLowerCase());
  } else if (mobileLike) {
    canonicalParams.set('mobile', value.replace(/[\s()-]/g, ''));
    canonicalParams.set('externalPatientId', value);
    canonicalParams.set('externalRegistrationNumber', value);
  } else {
    // The upgraded backend combines supplied fields with OR. This preserves
    // prefix name search while also allowing exact Citizen/HMIS identifiers.
    canonicalParams.set('name', value);
    canonicalParams.set('citizenId', value);
    canonicalParams.set('externalPatientId', value);
    canonicalParams.set('externalRegistrationNumber', value);
  }

  return [
    `/api/citizens/search?${canonicalParams.toString()}`,

    // Legacy compatibility endpoints remain as fallbacks while older tenants
    // are upgraded. They are not used when the canonical endpoint succeeds.
    `/api/citizens/search?q=${encoded}`,
    `/api/citizens?search=${encoded}`,
  ];
}

async function getPayload(url, signal) {
  const response = await http.get(url, signal ? { signal } : undefined);
  const status = statusOf(response);
  const payload = responsePayload(response);

  if (response?.ok === false || status >= 400 || payload?.success === false) {
    const error = new Error(errorMessage(payload, 'Search failed'));
    error.status = status;
    throw error;
  }

  return payload;
}

async function searchCitizens(query, signal) {
  const urls = buildSearchUrls(query);
  let lastError = null;

  for (let index = 0; index < urls.length; index += 1) {
    try {
      const payload = await getPayload(urls[index], signal);
      const results = deduplicateCitizens(normalizeResults(payload));

      // A successful canonical response, including an empty result set, is
      // authoritative. Fallbacks are used only for missing/unsupported routes.
      if (index === 0 || results.length > 0) return results;
    } catch (error) {
      if (isAbortError(error)) throw error;
      lastError = error;
      const status = statusOf(error) || Number(error?.status || 0);

      if (![404, 405].includes(status) && index === 0) {
        throw error;
      }
    }
  }

  const finalStatus = statusOf(lastError) || Number(lastError?.status || 0);
  if (finalStatus === 404) return [];
  if (lastError) throw lastError;
  return [];
}

function normalizeCentre(value) {
  const original = stringValue(value);
  if (!original) return '';

  const normalized = original
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.includes('keru')) return 'CRHA Keru';
  if (normalized.includes('dhawa')) return 'CRHA Dhawa';
  if (normalized.includes('pratap')) return 'UHTC Pratap Nagar';
  if (
    ['dundhara', 'dhundhara', 'dundara', 'dhundara'].some((item) =>
      normalized.includes(item)
    )
  ) {
    return 'CRHA Dhundhara';
  }

  return original;
}

function normalizeCitizen(item) {
  const source = unwrapCitizen(item);
  if (!source || typeof source !== 'object') return null;

  const citizenId = stringValue(
    source.citizenId || source.id || source._id || source.cid
  );
  if (!citizenId) return null;

  return {
    ...source,
    citizenId,
    name:
      source.name ||
      source.fullName ||
      `${source.firstName || ''} ${source.lastName || ''}`.trim(),
    age: source.age ?? source?.demographics?.age,
    gender: source.gender ?? source.sex ?? source?.demographics?.gender,
    mobile: source.mobile || source.phone || '',
    email: source.email || '',
    address: source.address || '',
    centre: normalizeCentre(source.centre || source.chc || source.healthCentre || ''),
  };
}

function isOperationsHubPath() {
  try {
    return /\/dashboard\/operations\/?$/i.test(window.location.pathname || '');
  } catch {
    return false;
  }
}

function isCitizenEncounterFlowPath() {
  try {
    const path = window.location.pathname || '';
    return (
      /\/citizen\/[^/]+\/(add-visit|previous-visits|upload-documents)\/?$/i.test(path) ||
      /\/citizen\/[^/]+\/encounter\/(history|examination|investigations|prescriptions)\/?$/i.test(path)
    );
  } catch {
    return false;
  }
}

function shouldStayOnCurrentFlow() {
  try {
    const path = window.location.pathname || '';
    return (
      /\/intern\/activity\/new\/?$/i.test(path) ||
      /\/dashboard\/operations\/?$/i.test(path)
    );
  } catch {
    return false;
  }
}

function displayAge(citizen) {
  if (!citizen) return '';

  const ageValue = citizen.ageValue;
  const ageUnit = stringValue(citizen.ageUnit);
  if (ageValue !== undefined && ageValue !== null && ageValue !== '') {
    const prefix = citizen.ageIsApproximate ? 'Approx. ' : '';
    return `${prefix}${ageValue} ${ageUnit || 'years'}`;
  }

  if (citizen.age !== undefined && citizen.age !== null && citizen.age !== '') {
    return `${citizen.age} years`;
  }

  return '';
}

function matchesLocalCitizen(citizen, query) {
  const needle = stringValue(query).toLowerCase();
  if (!needle) return false;

  return [
    citizen?.citizenId,
    citizen?.id,
    citizen?._id,
    citizen?.name,
    citizen?.fullName,
    citizen?.mobile,
    citizen?.phone,
    citizen?.email,
    citizen?.externalPatientId,
    citizen?.externalRegistrationNumber,
  ].some((value) => stringValue(value).toLowerCase().includes(needle));
}

function extractCitizenIdFromQr(rawValue) {
  const raw = stringValue(rawValue);
  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw);
    const fromJson = stringValue(
      parsed?.citizenId || parsed?.cid || parsed?.id || parsed?.subject
    );
    if (fromJson) return fromJson;
  } catch {
    // Plain-text QR values are expected in the current deployment.
  }

  try {
    const url = new URL(raw, window.location.origin);
    const fromQuery = stringValue(
      url.searchParams.get('citizenId') ||
        url.searchParams.get('cid') ||
        url.searchParams.get('id')
    );
    if (fromQuery) return fromQuery;

    const pathMatch = url.pathname.match(/\b(CIT-[A-Za-z0-9-]+)\b/i);
    if (pathMatch?.[1]) return pathMatch[1];
  } catch {
    // Not a URL; continue with prefix/plain-text handling.
  }

  const withoutPrefix = raw.replace(/^(CID|CITIZEN_ID|CITIZEN)\s*:\s*/i, '');
  return stringValue(withoutPrefix);
}

export default function CitizenSearchBar({ onResult }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [scanning, setScanning] = useState(false);

  const lastFetchedRef = useRef([]);
  const composingRef = useRef(false);
  const fetchAbortRef = useRef(null);
  const searchSequenceRef = useRef(0);
  const boxRef = useRef(null);
  const videoRef = useRef(null);
  const detectorRef = useRef(null);
  const scanningRef = useRef(false);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);

  const { setCitizen } = useCitizen();
  const navigate = useNavigate();

  const canScan = useMemo(
    () => typeof window !== 'undefined' && 'BarcodeDetector' in window,
    []
  );

  const stopScanner = useCallback(() => {
    scanningRef.current = false;
    setScanning(false);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const stream = streamRef.current || videoRef.current?.srcObject;
    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;

    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {}
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };

    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (canScan) {
      try {
        detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
      } catch {
        detectorRef.current = null;
      }
    }

    return () => {
      stopScanner();
    };
  }, [canScan, stopScanner]);

  useEffect(() => {
    if (composingRef.current) return undefined;

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < MIN_CHARS) {
      fetchAbortRef.current?.abort?.();
      setItems([]);
      setOpen(false);
      setLoading(false);
      setSearchError('');
      return undefined;
    }

    const timer = setTimeout(async () => {
      fetchAbortRef.current?.abort?.();
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      const sequence = searchSequenceRef.current + 1;
      searchSequenceRef.current = sequence;

      setLoading(true);
      setSearchError('');
      setOpen(true);

      try {
        let results = await searchCitizens(trimmedQuery, controller.signal);

        if (!results.length && lastFetchedRef.current.length) {
          results = lastFetchedRef.current.filter((citizen) =>
            matchesLocalCitizen(citizen, trimmedQuery)
          );
        } else if (results.length) {
          lastFetchedRef.current = results;
        }

        if (
          controller.signal.aborted ||
          sequence !== searchSequenceRef.current
        ) {
          return;
        }

        setItems(results);
        setOpen(true);
      } catch (error) {
        if (isAbortError(error)) return;
        if (sequence !== searchSequenceRef.current) return;

        setItems([]);
        setOpen(true);
        setSearchError(errorMessage(error, 'Unable to search citizens.'));
      } finally {
        if (
          !controller.signal.aborted &&
          sequence === searchSequenceRef.current
        ) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(
    () => () => {
      fetchAbortRef.current?.abort?.();
    },
    []
  );

  const persistCitizen = useCallback((citizen) => {
    try {
      const previousCitizen = (() => {
        try {
          return JSON.parse(localStorage.getItem('activeCitizen') || 'null') || {};
        } catch {
          return {};
        }
      })();
      const previousCitizenId = stringValue(previousCitizen?.citizenId);
      const previousVisitId = stringValue(
        localStorage.getItem('activeVisitId') || localStorage.getItem('visitId')
      );

      if (previousCitizenId) {
        localStorage.removeItem(`activeVisitId:${previousCitizenId}`);
      }
      if (previousVisitId) {
        localStorage.removeItem(`activeVisitContext:${previousVisitId}`);
      }

      localStorage.removeItem('activeVisitId');
      localStorage.removeItem('visitId');
      localStorage.removeItem('activeVisitContext');
      localStorage.removeItem('editReturnedVisit');
      localStorage.removeItem('selectedVisit');

      localStorage.setItem('activeCitizen', JSON.stringify(citizen));
      localStorage.setItem('activeCitizenId', citizen.citizenId);
      localStorage.setItem('lastVisitedCitizenId', citizen.citizenId);
      localStorage.removeItem(`activeVisitId:${citizen.citizenId}`);

      if (citizen.centre) {
        localStorage.setItem('activeCentre', citizen.centre);
        localStorage.setItem('centre', citizen.centre);
      }
    } catch {
      // Browser-storage failure should not prevent the in-memory selection.
    }
  }, []);

  const pick = useCallback(
    (item) => {
      const citizen = normalizeCitizen(item);
      if (!citizen?.citizenId) return;

      setOpen(false);
      setItems([]);
      setSearchError('');
      fetchAbortRef.current?.abort?.();
      setQuery('');

      persistCitizen(citizen);
      setCitizen(citizen);
      if (typeof onResult === 'function') onResult(citizen);

      const state = {
        citizenId: citizen.citizenId,
        citizen,
        selectedCitizen: citizen,
        fromCitizenSearchBar: true,
      };

      if (
        (shouldStayOnCurrentFlow() || isCitizenEncounterFlowPath()) &&
        typeof onResult === 'function'
      ) {
        return;
      }

      const target = `${tenantPrefix()}/dashboard/operations`;
      if (isOperationsHubPath()) {
        navigate(target, { replace: true, state });
        return;
      }

      navigate(target, { replace: true, state });
    },
    [navigate, onResult, persistCitizen, setCitizen]
  );

  const loadCitizenById = useCallback(
    async (citizenId) => {
      const response = await http.get(
        `/api/citizens/${encodeURIComponent(citizenId)}`
      );
      const status = statusOf(response);
      const payload = responsePayload(response);

      if (response?.ok === false || status >= 400 || payload?.success === false) {
        const error = new Error(errorMessage(payload, 'Citizen not found.'));
        error.status = status;
        throw error;
      }

      const citizen = normalizeCitizen(unwrapCitizen(payload));
      if (!citizen?.citizenId) {
        throw new Error('Citizen details were not returned for the scanned QR code.');
      }

      pick(citizen);
    },
    [pick]
  );

  const scanFrame = useCallback(
    async (timestamp) => {
      if (!scanningRef.current) return;

      const video = videoRef.current;
      const detector = detectorRef.current;
      if (!video || !detector) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      const previousTimestamp = Number(video.dataset.lastScanTimestamp || 0);
      if (timestamp - previousTimestamp < 150) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }
      video.dataset.lastScanTimestamp = String(timestamp);

      try {
        if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
          animationFrameRef.current = requestAnimationFrame(scanFrame);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        context?.drawImage(video, 0, 0, canvas.width, canvas.height);

        const barcodes = await detector.detect(canvas);
        if (barcodes?.[0]) {
          const citizenId = extractCitizenIdFromQr(barcodes[0].rawValue);
          stopScanner();

          if (!citizenId) {
            setSearchError('The QR code did not contain a Citizen ID.');
            setOpen(true);
            return;
          }

          try {
            await loadCitizenById(citizenId);
          } catch (error) {
            setQuery(citizenId);
            setSearchError(
              errorMessage(error, 'Unable to load the scanned Citizen ID.')
            );
            setOpen(true);
          }
          return;
        }
      } catch {
        // A single detection failure should not stop the camera loop.
      }

      if (scanningRef.current) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
      }
    },
    [loadCitizenById, stopScanner]
  );

  const startScan = useCallback(async () => {
    if (!canScan || !detectorRef.current) {
      window.alert('QR scan is not supported on this browser.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      window.alert('Camera access is not available on this device.');
      return;
    }

    try {
      setSearchError('');
      scanningRef.current = true;
      setScanning(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;

      if (!videoRef.current) {
        stopScanner();
        throw new Error('Camera preview is unavailable.');
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      videoRef.current.dataset.lastScanTimestamp = '0';
      animationFrameRef.current = requestAnimationFrame(scanFrame);
    } catch (error) {
      stopScanner();
      window.alert(
        error?.message || 'Could not access the camera. Check browser permissions.'
      );
    }
  }, [canScan, scanFrame, stopScanner]);

  const noMatches = useMemo(
    () => open && !loading && !searchError && query.trim().length >= MIN_CHARS && items.length === 0,
    [open, loading, searchError, query, items.length]
  );

  return (
    <Box
      ref={boxRef}
      sx={{ position: 'relative', display: 'flex', gap: 1, alignItems: 'center' }}
    >
      <TextField
        size="small"
        fullWidth
        label="Search citizen (name / mobile / Citizen ID / HMIS ID)"
        value={query}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={(event) => {
          composingRef.current = false;
          setQuery(event.currentTarget.value);
        }}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => {
          if (query.trim().length >= MIN_CHARS) setOpen(true);
        }}
        inputProps={{ autoComplete: 'off' }}
      />

      <Tooltip title={canScan ? 'Scan citizen identification QR' : 'QR scan not supported'}>
        <span>
          <IconButton onClick={startScan} disabled={!canScan} aria-label="Scan citizen QR">
            <QrCodeScannerIcon />
          </IconButton>
        </span>
      </Tooltip>

      {scanning && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Citizen QR scanner"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.72)',
            zIndex: 1300,
            display: 'grid',
            placeItems: 'center',
            padding: 16,
          }}
        >
          <div style={{ position: 'relative', width: 'min(92vw, 720px)' }}>
            <video
              ref={videoRef}
              playsInline
              muted
              style={{ width: '100%', borderRadius: 12, background: '#111' }}
            />
            <button
              type="button"
              onClick={stopScanner}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                padding: '6px 10px',
                borderRadius: 8,
                background: '#444',
                color: 'white',
                border: 0,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {open && (
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            zIndex: 20,
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 360,
            overflowY: 'auto',
            mt: 0.5,
          }}
        >
          {loading && (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <span>Searching…</span>
            </Box>
          )}

          {!loading && searchError && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="error">
                {searchError}
              </Typography>
            </Box>
          )}

          {!loading && !searchError && (
            <List dense disablePadding>
              {items.map((item, index) => {
                const citizen = normalizeCitizen(item);
                if (!citizen) return null;

                const age = displayAge(citizen);
                const key = citizen.citizenId || citizen._id || index;
                const secondary = [
                  citizen.citizenId ? `ID: ${citizen.citizenId}` : null,
                  citizen.mobile ? `Mobile: ${citizen.mobile}` : null,
                  citizen.gender ? `Gender: ${citizen.gender}` : null,
                  age ? `Age: ${age}` : null,
                  citizen.centre ? `Centre: ${citizen.centre}` : null,
                  citizen.externalPatientId
                    ? `HMIS Patient ID: ${citizen.externalPatientId}`
                    : null,
                  citizen.externalRegistrationNumber
                    ? `HMIS Registration: ${citizen.externalRegistrationNumber}`
                    : null,
                ]
                  .filter(Boolean)
                  .join('  •  ');

                return (
                  <ListItemButton key={key} onClick={() => pick(citizen)}>
                    <ListItemText
                      primary={citizen.name || citizen.citizenId}
                      secondary={secondary}
                    />
                  </ListItemButton>
                );
              })}

              {noMatches && (
                <Box sx={{ p: 2, color: 'text.secondary' }}>
                  No matching citizen found.
                </Box>
              )}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
}
